// Utility to detect a branch's school level. Reads the `school_level`
// value that the branch login API returns (stored on userData in
// localStorage). Falls back to 'primary' if absent — keeps the app
// working for users who logged in before the column existed.
//
// Possible values: 'high' | 'primary' | 'both'.
// 'both' branches see all year levels (no filter).

export const LEVEL_HIGH = "high";
export const LEVEL_PRIMARY = "primary";
export const LEVEL_BOTH = "both";

const HIGH_YEARS = ["7", "8", "9", "10", "11", "12"];
const PRIMARY_YEARS = ["0", "1", "2", "3", "4", "5", "6"];

// Year 6 is primary with one exception: in Term 4 Year 6 students start the
// Year 7 courses a term early ("Y6/7 Maths"), at the high school campuses.
// So a high school campus gets Year 6 too, but only for those courses; the
// API marks them high_school and only offers them in Term 4.
const BRIDGE_YEAR = "6";

const readSchoolLevel = () => {
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) return LEVEL_PRIMARY;
    const parsed = JSON.parse(raw);
    const lvl = parsed?.data?.school_level;
    if (lvl === LEVEL_HIGH || lvl === LEVEL_PRIMARY || lvl === LEVEL_BOTH) {
      return lvl;
    }
  } catch {
    // ignore
  }
  return LEVEL_PRIMARY;
};

export const getBranchLevel = () => readSchoolLevel();

// Allowed Year *names* (string) for the current branch level.
export const allowedGradeNames = () => {
  const lvl = readSchoolLevel();
  if (lvl === LEVEL_HIGH) return [BRIDGE_YEAR, ...HIGH_YEARS];
  if (lvl === LEVEL_BOTH) return [...PRIMARY_YEARS, ...HIGH_YEARS];
  return PRIMARY_YEARS;
};

// Predicate helpers — accept either grade row { name } or a raw grade name.
export const gradeAllowed = (gradeOrName) => {
  const name = typeof gradeOrName === "object" ? String(gradeOrName?.name) : String(gradeOrName);
  return allowedGradeNames().includes(name);
};

// For a course or class row: its year name and the API's high_school flag.
// A high school campus sees Year 6 rows only when they are high school ones.
export const courseAllowed = (gradeName, highSchool) => {
  const name = String(gradeName);
  if (readSchoolLevel() === LEVEL_HIGH && name === BRIDGE_YEAR) return Number(highSchool) === 1;
  return gradeAllowed(name);
};

// Shown under Grade when a high school campus picks Year 6.
export const bridgeYearNote = (gradeName) =>
  readSchoolLevel() === LEVEL_HIGH && String(gradeName) === BRIDGE_YEAR
    ? "Year 6 is for the Term 4 Y6/7 courses only."
    : "";
